import React from "react";

import PageWrapper from "../../../layout/PageWrapper/PageWrapper";
import Page from "../../../layout/Page/Page";
import Card, {
  CardBody,
  CardHeader,
  CardLabel,
  CardTitle,
} from "../../../components/bootstrap/Card";
import Button from "../../../components/bootstrap/Button";
import notificationState from "../../../shared/mobx/notification-state";
import { PLUGIN_DOWNLOAD_URL } from "../../../shared/constants/globalContants";

const DownloadPluginPage: React.FC = () => {
  const [isDownloading, setIsDownloading] = React.useState(false);
  const triggerDirectDownload = () => {
    const link = document.createElement("a");
    link.href = PLUGIN_DOWNLOAD_URL;
    link.download = "web2pdf.zip";
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const isZipResponse = async (response: Response, blob: Blob) => {
    const contentType = (response.headers.get("content-type") || "").toLowerCase();
    if (
      contentType.includes("application/zip") ||
      contentType.includes("application/x-zip-compressed") ||
      contentType.includes("application/octet-stream")
    ) {
      return true;
    }

    const signature = await blob.slice(0, 4).arrayBuffer();
    const bytes = new Uint8Array(signature);
    // ZIP local file header starts with PK\x03\x04
    return (
      bytes.length >= 4 &&
      bytes[0] === 0x50 &&
      bytes[1] === 0x4b &&
      bytes[2] === 0x03 &&
      bytes[3] === 0x04
    );
  };

  const handleDownloadPlugin = async () => {
    try {
      setIsDownloading(true);
      let response: Response;
      try {
        response = await fetch(PLUGIN_DOWNLOAD_URL, { method: "GET" });
      } catch (fetchError) {
        // Common in local dev when cross-origin fetch is blocked by CORS.
        console.warn("Plugin fetch blocked, using direct download:", fetchError);
        triggerDirectDownload();
        notificationState.addToasterInfo("Download started in a new tab.");
        return;
      }

      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }

      const blob = await response.blob();
      const validZip = await isZipResponse(response, blob);
      if (!validZip) {
        throw new Error("Invalid plugin archive response");
      }

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = "webs2pdf.zip";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      notificationState.addToasterSuccess("Plugin download started.");
    } catch (error) {
      console.error("Plugin download error:", error);
      notificationState.addToasterError(
        "Plugin file is unavailable right now. Please contact support."
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <PageWrapper title="Download Plugin">
      <Page container="fluid">
        <div className="row">
          <div className="col-12">
            <Card>
              <CardHeader borderSize={1}>
                <CardLabel icon="Download" iconColor="primary">
                  <CardTitle tag="div" className="h5">
                    Download WordPress Plugin
                  </CardTitle>
                </CardLabel>
              </CardHeader>
              <CardBody>
                <p className="mb-3 text-dark">
                  Click the button below to download the Webs2pdf WordPress
                  plugin ZIP file.
                </p>
                <Button
                  color="primary"
                  icon="Download"
                  className="auth-btn"
                  isDisable={isDownloading}
                  onClick={handleDownloadPlugin}
                >
                  {isDownloading ? "Downloading..." : "Download WordPress Plugin"}
                </Button>
                <hr className="my-4" />
                <h6 className="mb-2">1. Install Plugin</h6>
                <ol className="mb-4">
                  <li>Download the plugin ZIP file using the button above.</li>
                  <li>
                    In WordPress Admin, go to <strong>Plugins</strong> &gt;{" "}
                    <strong>Add New</strong>.
                  </li>
                  <li>
                    Click <strong>Upload Plugin</strong>, choose{" "}
                    <code>webs2pdf.zip</code>, and click{" "}
                    <strong>Install Now</strong>.
                  </li>
                </ol>

                <h6 className="mb-2">2. Activate Plugin</h6>
                <ol className="mb-4">
                  <li>
                    After installation, click <strong>Activate Plugin</strong>.
                  </li>
                  <li>
                    Open plugin settings from the WordPress left menu (Webs to Pdf
                    section).
                  </li>
                  <li>
                    Add your API key from the <strong>Manage API Keys</strong>{" "}
                    page in this dashboard and save settings.
                  </li>
                </ol>

                <h6 className="mb-2">3. Usage</h6>
                <ol className="mb-0">
                  <li>
                    In WordPress Admin sidebar, go to{" "}
                    <strong>Webs to Pdf</strong> and click{" "}
                    <strong>Convert to PDF</strong>.
                  </li>
                  <li>
                    The plugin will load all available Pages and Posts. Select
                    one or multiple items you want to convert.
                  </li>
                  <li>
                    Choose your PDF options (for example format, margin, size,
                    and related conversion settings).
                  </li>
                  <li>
                    Click generate to create PDF(s), then download and verify
                    the output.
                  </li>
                  <li>
                    If generation fails, recheck API key status and plugin
                    settings.
                  </li>
                </ol>
              </CardBody>
            </Card>
          </div>
        </div>
      </Page>
    </PageWrapper>
  );
};

export default DownloadPluginPage;
